import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { childService } from '../services/childService'
import { sharingService } from '../services/sharingService'
import { useAuth } from './AuthContext'

const ChildContext = createContext()

// Action types
const CHILD_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_CHILDREN: 'SET_CHILDREN',
  SET_ACTIVE_CHILD: 'SET_ACTIVE_CHILD',
  ADD_CHILD: 'ADD_CHILD',
  UPDATE_CHILD: 'UPDATE_CHILD',
  REMOVE_CHILD: 'REMOVE_CHILD',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
}

// Initial state
const initialState = {
  children: [],
  activeChild: null,
  loading: false,
  error: null,
}

// Reducer function
function childReducer(state, action) {
  switch (action.type) {
    case CHILD_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case CHILD_ACTIONS.SET_CHILDREN:
      return {
        ...state,
        children: action.payload,
        loading: false,
        error: null,
      }

    case CHILD_ACTIONS.SET_ACTIVE_CHILD:
      return {
        ...state,
        activeChild: action.payload,
      }

    case CHILD_ACTIONS.ADD_CHILD:
      return {
        ...state,
        children: [...state.children, action.payload],
        error: null,
      }

    case CHILD_ACTIONS.UPDATE_CHILD:
      return {
        ...state,
        children: state.children.map(child =>
          child.id === action.payload.id ? action.payload : child
        ),
        activeChild: state.activeChild?.id === action.payload.id
          ? action.payload
          : state.activeChild,
        error: null,
      }

    case CHILD_ACTIONS.REMOVE_CHILD:
      return {
        ...state,
        children: state.children.filter(child => child.id !== action.payload),
        activeChild: state.activeChild?.id === action.payload ? null : state.activeChild,
        error: null,
      }

    case CHILD_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      }

    case CHILD_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }

    default:
      return state
  }
}

// Provider component
export function ChildProvider({ children }) {
  const [state, dispatch] = useReducer(childReducer, initialState)
  const { user, isAuthenticated, loading: authLoading } = useAuth()

  // Load children when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      loadChildren()
    } else if (!isAuthenticated && !authLoading) {
      // Clear children data when user logs out
      dispatch({ type: CHILD_ACTIONS.SET_CHILDREN, payload: [] })
      dispatch({ type: CHILD_ACTIONS.SET_ACTIVE_CHILD, payload: null })
    }
  }, [isAuthenticated, user, authLoading])

  // Load active child from localStorage on mount
  useEffect(() => {
    const savedActiveChildId = localStorage.getItem('activeChildId')
    if (savedActiveChildId && state.children.length > 0) {
      const activeChild = state.children.find(child => child.id === parseInt(savedActiveChildId))
      if (activeChild) {
        dispatch({ type: CHILD_ACTIONS.SET_ACTIVE_CHILD, payload: activeChild })
      }
    } else if (state.children.length > 0 && !state.activeChild) {
      // Set first child as active if no active child is set
      setActiveChild(state.children[0])
    }
  }, [state.children])

  // Actions
  const loadChildren = async () => {
    if (!isAuthenticated || !user) {
      console.warn('Cannot load children: user not authenticated')
      return
    }

    try {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: true })
      const [response, sharedRaw] = await Promise.all([
        childService.getChildren(),
        sharingService.getSharedChildren().catch(() => []),
      ])

      const ownChildren = response.results || response
      const sharedChildren = (sharedRaw.results || sharedRaw).map(share => ({
        id: share.child_id,
        name: share.child_name,
        date_of_birth: share.child_date_of_birth,
        gender: share.child_gender,
        profile_picture: share.child_profile_picture,
        shared: true,
        share_id: share.id,
        permission_level: share.permission_level,
        owner_name: share.owner_name,
        owner_email: share.owner_email,
      }))

      const ownIds = new Set(ownChildren.map(c => c.id))
      const merged = [
        ...ownChildren,
        ...sharedChildren.filter(c => !ownIds.has(c.id)),
      ]

      dispatch({ type: CHILD_ACTIONS.SET_CHILDREN, payload: merged })
    } catch (error) {
      console.error('Error loading children:', error)
      dispatch({
        type: CHILD_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to load children'
      })
    }
  }

  const createChild = async (childData) => {
    try {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: true })
      const newChild = await childService.createChild(childData)
      dispatch({ type: CHILD_ACTIONS.ADD_CHILD, payload: newChild })

      // Set as active child if it's the first child
      if (state.children.length === 0) {
        setActiveChild(newChild)
      }

      return newChild
    } catch (error) {
      dispatch({
        type: CHILD_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to create child'
      })
      throw error
    } finally {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: false })
    }
  }

  const updateChild = async (childId, childData) => {
    try {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: true })
      const updatedChild = await childService.updateChild(childId, childData)
      dispatch({ type: CHILD_ACTIONS.UPDATE_CHILD, payload: updatedChild })
      return updatedChild
    } catch (error) {
      dispatch({
        type: CHILD_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to update child'
      })
      throw error
    } finally {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: false })
    }
  }

  const deleteChild = async (childId) => {
    try {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: true })
      await childService.deleteChild(childId)
      dispatch({ type: CHILD_ACTIONS.REMOVE_CHILD, payload: childId })
    } catch (error) {
      dispatch({
        type: CHILD_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to delete child'
      })
      throw error
    } finally {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: false })
    }
  }

  const uploadPhoto = async (childId, photoFile) => {
    try {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: true })
      const response = await childService.uploadPhoto(childId, photoFile)
      const updatedChild = response.child
      dispatch({ type: CHILD_ACTIONS.UPDATE_CHILD, payload: updatedChild })
      return updatedChild
    } catch (error) {
      dispatch({
        type: CHILD_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to upload photo'
      })
      throw error
    } finally {
      dispatch({ type: CHILD_ACTIONS.SET_LOADING, payload: false })
    }
  }

  const setActiveChild = (child) => {
    dispatch({ type: CHILD_ACTIONS.SET_ACTIVE_CHILD, payload: child })
    if (child) {
      localStorage.setItem('activeChildId', child.id.toString())
    } else {
      localStorage.removeItem('activeChildId')
    }
  }

  const clearError = () => {
    dispatch({ type: CHILD_ACTIONS.CLEAR_ERROR })
  }

  const value = {
    ...state,
    loadChildren,
    createChild,
    updateChild,
    deleteChild,
    uploadPhoto,
    setActiveChild,
    clearError,
  }

  return (
    <ChildContext.Provider value={value}>
      {children}
    </ChildContext.Provider>
  )
}

// Custom hook to use the child context
export function useChild() {
  const context = useContext(ChildContext)
  if (!context) {
    throw new Error('useChild must be used within a ChildProvider')
  }
  return context
}